import React, { useState, useEffect } from 'react';
import './FormularioLote.css';
import { toast } from 'react-toastify';
import { authService } from '../services/auth';
import { planoService } from '../services/planoService';
import { loteService } from '../services/api';

/**
 * Componente FormularioLote
 * Formulario para crear o editar un lote
 * Calcula automáticamente el precio total
 */
const FormularioLote = ({ lote, onSave, onCancel, currentPlano }) => {
  const [formData, setFormData] = useState({
    numero: '',
    area: '',
    precioPorMetro: '',
    estado: 'disponible',
    tipoForma: 'rectangulo',
    geometria: {
      x: 0,
      y: 0,
      width: 100,
      height: 100,
    },
    // Mantener coordenadas para compatibilidad
    coordenadas: {
      x: 0,
      y: 0,
      width: 100,
      height: 100,
    },
    cliente: {
      nombre: '',
      telefono: '',
      email: '',
    },
    notas: '',
  });

  const [precioTotal, setPrecioTotal] = useState(0);
  const [planos, setPlanos] = useState([]);
  const [selectedPlano, setSelectedPlano] = useState(null);
  useEffect(() => {
    if (lote) {
      const tipoForma = lote.tipoForma || 'rectangulo';
      let geometria = lote.geometria || { x: 0, y: 0, width: 100, height: 100 };

      // Si no tiene geometria pero tiene coordenadas, usar coordenadas
      if (!lote.geometria && lote.coordenadas) {
        geometria = lote.coordenadas;
      }

      setFormData({
        numero: lote.numero || '',
        area: lote.area || '',
        precioPorMetro: lote.precioPorMetro || '',
        estado: lote.estado || 'disponible',
        tipoForma: tipoForma,
        geometria: geometria,
        coordenadas: lote.coordenadas || geometria,
        cliente: lote.cliente || { nombre: '', telefono: '', email: '' },
        notas: lote.notas || '',
      });

      // Si el lote ya tiene asociado un plano, preseleccionarlo
      if (lote.plano) {
        setSelectedPlano(lote.plano);
      }
    } else {
      // Si no hay lote (creación), si se recibe currentPlano desde props, preseleccionarlo
      if (typeof currentPlano !== 'undefined' && currentPlano && currentPlano._id) {
        setSelectedPlano(currentPlano._id);
      }
    }
  }, [lote, currentPlano]);

  useEffect(() => {
    // Recalcular precio total cuando cambian área o precio por metro
    const area = parseFloat(formData.area) || 0;
    const precioPorMetro = parseFloat(formData.precioPorMetro) || 0;
    setPrecioTotal(area * precioPorMetro);
  }, [formData.area, formData.precioPorMetro]);

  // Cargar planos del usuario para selección
  useEffect(() => {
    const cargar = async () => {
      try {
        const data = await planoService.getPlanos();
        setPlanos(data);
        // Si no hay lote y hay currentPlano en props, asegurarnos de preseleccionar
        if (!lote && typeof currentPlano !== 'undefined' && currentPlano && currentPlano._id) {
          setSelectedPlano(currentPlano._id);
        }

        // Si estamos creando (no hay lote) pedir el siguiente número y prefilar si no existe
        if (!lote) {
          try {
            const next = await loteService.obtenerSiguienteNumero();
            if (next) setFormData((prev) => ({ ...prev, numero: next }));
          } catch (err) {
            // No crítico
          }
        }
      } catch (err) {
        console.error('Error al cargar planos en formulario:', err);
      }
    };
    cargar();
  }, [currentPlano, lote]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'plano') {
      setSelectedPlano(value || null);
      return;
    }

    if (name.startsWith('cliente.')) {
      const clienteField = name.split('.')[1];
      setFormData((prev) => ({
        ...prev,
        cliente: {
          ...prev.cliente,
          [clienteField]: value,
        },
      }));
    } else if (name.startsWith('geometria.')) {
      const geomField = name.split('.')[1];
      setFormData((prev) => {
        const nuevaGeometria = {
          ...prev.geometria,
          [geomField]: parseFloat(value) || 0,
        };
        // Actualizar coordenadas también para compatibilidad
        const nuevasCoordenadas =
          prev.tipoForma === 'rectangulo' ? nuevaGeometria : prev.coordenadas;
        return {
          ...prev,
          geometria: nuevaGeometria,
          coordenadas: nuevasCoordenadas,
        };
      });
    } else if (name === 'tipoForma') {
      // Resetear geometría cuando cambia el tipo de forma
      setFormData((prev) => {
        let nuevaGeometria = {};
        if (value === 'rectangulo') {
          nuevaGeometria = { x: 0, y: 0, width: 100, height: 100 };
        } else if (value === 'circulo') {
          nuevaGeometria = { x: 0, y: 0, radius: 50 };
        } else if (value === 'poligono') {
          nuevaGeometria = {
            puntos: [
              { x: 0, y: 0 },
              { x: 100, y: 0 },
              { x: 100, y: 100 },
              { x: 0, y: 100 },
            ],
          };
        }
        return {
          ...prev,
          tipoForma: value,
          geometria: nuevaGeometria,
        };
      });
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validación básica (el número se puede generar automáticamente si está vacío)
    if (!formData.area || !formData.precioPorMetro) {
      toast.error('Por favor completa todos los campos obligatorios');
      return;
    }

    // Si el lote se marca como reservado o vendido, exigir datos de cliente
    if (formData.estado === 'reservado' || formData.estado === 'vendido') {
      const { nombre, telefono, email } = formData.cliente || {};
      if (!nombre || !telefono || !email) {
        toast.error('Para lotes reservados o vendidos debes ingresar nombre, teléfono y email del cliente.');
        return;
      }
    }

    // Requerir que el usuario esté autenticado para crear/editar
    const user = authService.getUser();
    if (!user) {
      toast.error('Debes iniciar sesión para crear o editar lotes');
      return;
    }

    const loteData = {
      ...formData,
      area: parseFloat(formData.area),
      precioPorMetro: parseFloat(formData.precioPorMetro),
      // Asegurar que geometria y coordenadas estén sincronizadas
      geometria: formData.geometria,
      coordenadas: formData.tipoForma === 'rectangulo' ? formData.geometria : formData.coordenadas,
      plano: selectedPlano || null,
    };

    onSave(loteData);
  };

  return (
    <div className="formulario-lote">
      <h2>{lote ? 'Editar Lote' : 'Nuevo Lote'}</h2>
      <form onSubmit={handleSubmit}>
        <div className="form-grid">
          <div className="form-group">
            <label>Número de Lote *</label>
            <input
              type="text"
              name="numero"
              value={formData.numero}
              onChange={handleChange}
              placeholder="Ej: L-001 (opcional)"
            />
            <small className="form-hint">Si dejas el número vacío, se asignará automáticamente (L-000, L-001, ... por usuario)</small>
          </div>

          <div className="form-group">
            <label>Área (m²) *</label>
            <input
              type="number"
              name="area"
              value={formData.area}
              onChange={handleChange}
              required
              min="0"
              step="0.01"
              placeholder="Ej: 150.50"
            />
          </div>

          <div className="form-group">
            <label>Precio por m² ($) *</label>
            <input
              type="number"
              name="precioPorMetro"
              value={formData.precioPorMetro}
              onChange={handleChange}
              required
              min="0"
              step="0.01"
              placeholder="Ej: 500"
            />
          </div>

          <div className="form-group">
            <label>Precio Total ($)</label>
            <input
              type="text"
              value={precioTotal.toLocaleString('es-ES', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
              readOnly
              className="readonly"
            />
            <small className="form-hint">Calculado automáticamente</small>
          </div>

          <div className="form-group">
            <label>Estado *</label>
            <select name="estado" value={formData.estado} onChange={handleChange} required>
              <option value="disponible">Disponible</option>
              <option value="reservado">Reservado</option>
              <option value="vendido">Vendido</option>
            </select>
          </div>

          <div className="form-group">
            <label>Plano (asociar al lote)</label>
            <select name="plano" value={selectedPlano || ''} onChange={handleChange}>
              <option value="">-- Ninguno --</option>
              {planos.map((p) => (
                <option key={p._id} value={p._id}>{p.originalName || p.filename}</option>
              ))}
            </select>
            <small className="form-hint">Selecciona a qué imagen (plano) pertenece este lote</small>
          </div>

          <div className="form-group full-width">
            <h3>Forma y Posición en el Plano</h3>
          </div>

          <div className="form-group">
            <label>Tipo de Forma *</label>
            <select name="tipoForma" value={formData.tipoForma} onChange={handleChange} required>
              <option value="rectangulo">Rectángulo</option>
              <option value="circulo">Círculo</option>
              <option value="poligono">Polígono</option>
            </select>
          </div>

          {/* Campos para Rectángulo */}
          {formData.tipoForma === 'rectangulo' && (
            <>
              <div className="form-group">
                <label>Posición X</label>
                <input
                  type="number"
                  name="geometria.x"
                  step="any"
                  value={formData.geometria.x || 0}
                  onChange={handleChange}
                  placeholder="0"
                />
              </div>

              <div className="form-group">
                <label>Posición Y</label>
                <input
                  type="number"
                  name="geometria.y"
                  step="any"
                  value={formData.geometria.y || 0}
                  onChange={handleChange}
                  placeholder="0"
                />
              </div>

              <div className="form-group">
                <label>Ancho</label>
                <input
                  type="number"
                  name="geometria.width"
                  value={formData.geometria.width || 100}
                  onChange={handleChange}
                  placeholder="100"
                  min="0"
                  step="any"
                />
              </div>

              <div className="form-group">
                <label>Alto</label>
                <input
                  type="number"
                  name="geometria.height"
                  value={formData.geometria.height || 100}
                  onChange={handleChange}
                  placeholder="100"
                  min="0"
                  step="any"
                />
              </div>
            </>
          )}

          {/* Campos para Círculo */}
          {formData.tipoForma === 'circulo' && (
            <>
              <div className="form-group">
                <label>Centro X</label>
                <input
                  type="number"
                  name="geometria.x"
                  value={formData.geometria.x || 0}
                  onChange={handleChange}
                  placeholder="0"
                />
              </div>

              <div className="form-group">
                <label>Centro Y</label>
                <input
                  type="number"
                  name="geometria.y"
                  value={formData.geometria.y || 0}
                  onChange={handleChange}
                  placeholder="0"
                />
              </div>

              <div className="form-group">
                <label>Radio</label>
                <input
                  type="number"
                  name="geometria.radius"
                  value={formData.geometria.radius || 50}
                  onChange={handleChange}
                  placeholder="50"
                  min="0"
                />
              </div>
            </>
          )}

          {/* Campos para Polígono */}
          {formData.tipoForma === 'poligono' && (
            <div className="form-group full-width">
              <label>Puntos del Polígono</label>
              <small className="form-hint">
                Edita los puntos en el plano interactivo. Formato: [x, y]
              </small>
              <textarea
                value={JSON.stringify(formData.geometria.puntos || [], null, 2)}
                readOnly
                rows="4"
                style={{ fontFamily: 'monospace', fontSize: '12px' }}
              />
              <small className="form-hint">
                Nota: Los polígonos se editan mejor desde el plano interactivo
              </small>
            </div>
          )}

          <div className="form-group full-width">
            <h3>Información del Cliente</h3>
          </div>

          <div className="form-group">
            <label>Nombre</label>
            <input
              type="text"
              name="cliente.nombre"
              value={formData.cliente.nombre}
              onChange={handleChange}
              placeholder="Nombre del cliente"
            />
          </div>

          <div className="form-group">
            <label>Teléfono</label>
            <input
              type="tel"
              name="cliente.telefono"
              value={formData.cliente.telefono}
              onChange={handleChange}
              placeholder="+1234567890"
            />
          </div>

          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              name="cliente.email"
              value={formData.cliente.email}
              onChange={handleChange}
              placeholder="cliente@email.com"
            />
          </div>

          <div className="form-group full-width">
            <label>Notas</label>
            <textarea
              name="notas"
              value={formData.notas}
              onChange={handleChange}
              rows="4"
              placeholder="Notas adicionales sobre el lote..."
            />
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="btn-save">
            {lote ? 'Actualizar' : 'Crear'} Lote
          </button>
          <button type="button" onClick={onCancel} className="btn-cancel">
            Cancelar
          </button>
        </div>
      </form>
    </div>
  );
};

export default FormularioLote;
